import React, { useRef } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";
import { CONST } from "../config/const";
import { ProductPartnerResultResponse } from "../api/productPartnerResult";
import { generatePageRange } from "./Table";

function getInitialMonthHeaders(): string[] {
  const now = new Date();
  const endMonth = now.getMonth() + 1;
  let startYear = now.getFullYear();
  let startMonth = endMonth - 5;
  if (startMonth <= 0) {
    startMonth += 12;
    startYear -= 1;
  }
  const headers: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const m = startMonth + i;
    const year = startYear + Math.floor((m - 1) / 12);
    const month = ((m - 1) % 12) + 1;
    headers.push(`${year}年${month}月`);
  }
  return headers;
}

const headerSx = {
  backgroundColor: "var(--color-ai)",
  color: "var(--color-white)",
  fontWeight: "bold",
  fontSize: "14px",
  whiteSpace: "nowrap" as const,
};

const bodySx = {
  fontSize: "12px",
  color: "var(--color-sumi, #374151)",
};

const TOTAL_CELL_BG = "var(--color-ai)";
const totalLabelSx = {
  ...bodySx,
  color: "var(--color-white)",
  fontWeight: 700 as const,
};

interface TableProductPartnerResultProps {
  data: ProductPartnerResultResponse | null;
  displayMode: "月別" | "日別";
  serchOpen: boolean;
  totalProductCount: number;
  displayNumber: number;
  setDisplayNumber: (n: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (n: number) => void;
  maxPageNumber: number;
  fetchResult: (overrideDisplayNumber?: number, overridePage?: number) => void;
}

function TableProductPartnerResult(props: TableProductPartnerResultProps) {
  const {
    data,
    displayMode,
    serchOpen,
    totalProductCount,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    fetchResult,
  } = props;

  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const tableHeaderRef1 = useRef<HTMLTableRowElement | null>(null);
  const tableHeaderRef2 = useRef<HTMLTableRowElement | null>(null);
  // ★cmd-po_1233 (PPR横追従修正): 追従の入力を実スクロール器(TableContainer)の scrollLeft へ変更。
  // ヘッダーは SP時 position:fixed ゆえ横スクロールしても動かないので、コンテナの scrollLeft ぶん左へずらして値列と揃える。
  // ★cmd-po_1233 rework1: thead 内の【全 fixed ヘッダー行】へ一括で left を適用する。
  // top:164 には ショット行(ref付き)と ストック行(ref無し) の2行があり、ref個別指定ではストック行が追従漏れした。
  // querySelectorAll で thead の全行を走査すれば ref の有無に依らず全行が追従する(将来ヘッダー行が増えても漏れない)。
  // 単一の TableContainer/Table/TableHead 構造ゆえ対象は当該ヘッダー行のみ(tbody は含まれない)。PCでは left を触らない。
  const handleHeaderFollow = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isSmartPhone) return;
    const left = `-${e.currentTarget.scrollLeft}px`;
    e.currentTarget.querySelectorAll("thead tr").forEach((row) => {
      (row as HTMLElement).style.left = left;
    });
  };
  const pageList = Array.from({ length: maxPageNumber }, (_, i) => (
    <MenuItem key={i + 1} value={i + 1}>
      {i + 1}
    </MenuItem>
  ));
  const pagedRows = data?.rows ?? [];
  const pages = generatePageRange(pageNumber, maxPageNumber);
  const start = (pageNumber - 1) * displayNumber + 1;
  const end = pagedRows ? start + pagedRows.length - 1 : start + displayNumber - 1;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxShadow: "var(--shadow-soft)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-md)",
        backgroundColor: "var(--color-white)",
      }}
    >
      <TableContainer
        component={Paper}
        elevation={isSmartPhone ? undefined : 0}
        onScroll={handleHeaderFollow}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          marginTop: isSmartPhone ? "200px" : undefined,
          position: undefined,
          bottom: undefined,
        }}
      >
        <Table stickyHeader size="small" sx={{ "& thead .MuiTableCell-root": { paddingTop: "8px", paddingBottom: "8px", fontSize: "11px", fontWeight: 600, color: "var(--color-white)" }, "& tbody .MuiTableCell-root": { paddingTop: "8px", paddingBottom: "8px" } }}>
          <TableHead>
            <TableRow
              ref={tableHeaderRef1}
              sx={{
                position: isSmartPhone ? "fixed" : undefined,
                top: isSmartPhone ? "128px" : undefined,
                zIndex: isSmartPhone ? 1 : undefined,
              }}
            >
              <TableCell
                sx={{
                  ...headerSx,
                  width: 220,
                  minWidth: 220,
                  zIndex: isSmartPhone ? 2 : 3,
                }}
              >
                <Box className="table-header">商材名</Box>
              </TableCell>
              <TableCell
                sx={{
                  ...headerSx,
                  width: 200,
                  minWidth: 200,
                  zIndex: isSmartPhone ? 2 : 3,
                }}
              >
                <Box className="table-header">パートナー</Box>
              </TableCell>
              {displayMode === "月別" && (
                <TableCell
                  sx={{
                    ...headerSx,
                    width: 80,
                    minWidth: 80,
                    zIndex: isSmartPhone ? 2 : 3,
                  }}
                >
                  <Box className="table-header">累計合計</Box>
                </TableCell>
              )}
              {displayMode === "日別" && (
                <>
                  <TableCell
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                    }}
                  >
                    <Box className="table-header">指定月合計</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                    }}
                  >
                    <Box className="table-header">指定月見込</Box>
                  </TableCell>
                </>
              )}
              {(data ? data.headers : getInitialMonthHeaders()).map(
                (header, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: 2,
                    }}
                  >
                    <Box className="table-header">{header}</Box>
                  </TableCell>
                ),
              )}
            </TableRow>
            {data && displayMode === "日別" ? (
              <>
                {/* ショット行 */}
                <TableRow
                  ref={tableHeaderRef2}
                  sx={{
                    position: isSmartPhone ? "fixed" : undefined,
                    top: isSmartPhone ? "164px" : undefined,
                    zIndex: isSmartPhone ? 1 : undefined,
                  }}
                >
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: "sticky" as const,
                      top: "41px", // 41=①②段実行高/82=41×2/padding・font変更時要見直し
                      width: 220,
                      minWidth: 220,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      borderLeft: "3px solid var(--color-kohaku, #b8860b)",
                      ...totalLabelSx,
                    }}
                  >
                    <Box className="table-header">合計</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "41px",
                      width: 200,
                      minWidth: 200,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...totalLabelSx,
                    }}
                  >
                    <Box className="table-header">ショット</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "41px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_total_shot ?? 0}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "41px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_forecast_shot ?? 0}
                    </Box>
                  </TableCell>
                  {(data.total_shot ?? []).map((count, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        position: "sticky" as const,
                        top: "41px",
                        width: 80,
                        minWidth: 80,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">{count}</Box>
                    </TableCell>
                  ))}
                </TableRow>
                {/* ストック行 */}
                <TableRow
                  sx={{
                    position: isSmartPhone ? "fixed" : undefined,
                    top: isSmartPhone ? "164px" : undefined,
                    zIndex: isSmartPhone ? 1 : undefined,
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "82px",
                      width: 200,
                      minWidth: 200,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...totalLabelSx,
                    }}
                  >
                    <Box className="table-header">ストック</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "82px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_total_stock ?? 0}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "82px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: TOTAL_CELL_BG,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_forecast_stock ?? 0}
                    </Box>
                  </TableCell>
                  {(data.total_stock ?? []).map((count, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        position: "sticky" as const,
                        top: "82px",
                        width: 80,
                        minWidth: 80,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">{count}</Box>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              data && (
                <>
                  {/* ショット行 */}
                  <TableRow
                    ref={tableHeaderRef2}
                    sx={{
                      position: isSmartPhone ? "fixed" : undefined,
                      top: isSmartPhone ? "164px" : undefined,
                      zIndex: isSmartPhone ? 1 : undefined,
                    }}
                  >
                    <TableCell
                      rowSpan={2}
                      sx={{
                        position: "sticky" as const,
                        top: "41px",
                        width: 220,
                        minWidth: 220,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">合計</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "41px",
                        width: 200,
                        minWidth: 200,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">ショット</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "41px",
                        width: 80,
                        minWidth: 80,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">
                        {data.total_period_total_shot ?? 0}
                      </Box>
                    </TableCell>
                    {(data.total_shot ?? []).map((count, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          position: "sticky" as const,
                          top: "41px",
                          width: 80,
                          minWidth: 80,
                          backgroundColor: TOTAL_CELL_BG,
                          ...bodySx,
                        }}
                      >
                        <Box className="table-header">{count}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* ストック行 */}
                  <TableRow
                    sx={{
                      position: isSmartPhone ? "fixed" : undefined,
                      top: isSmartPhone ? "164px" : undefined,
                      zIndex: isSmartPhone ? 1 : undefined,
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "82px",
                        width: 200,
                        minWidth: 200,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">ストック</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "82px",
                        width: 80,
                        minWidth: 80,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: TOTAL_CELL_BG,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">
                        {data.total_period_total_stock ?? 0}
                      </Box>
                    </TableCell>
                    {(data.total_stock ?? []).map((count, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          position: "sticky" as const,
                          top: "82px",
                          width: 80,
                          minWidth: 80,
                          backgroundColor: TOTAL_CELL_BG,
                          ...bodySx,
                        }}
                      >
                        <Box className="table-header">{count}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </>
              )
            )}
          </TableHead>
          <TableBody>
            {data &&
              (() => {
                let globalPartnerIdx = 0;
                return pagedRows.map((row, productIdx) =>
                  (row.partners ?? []).map((partner, partnerIdx) => {
                    const productGroupBg =
                      productIdx % 2 === 1
                        ? "var(--color-kohaku-muted, #e8d4b8)"
                        : "white";
                    const currentGlobalIdx = globalPartnerIdx++;
                    const partnerRowBg =
                      currentGlobalIdx % 2 === 1
                        ? CONST.tableBackgroundColor
                        : "white";
                    return (
                      <TableRow
                        key={`${row.product_id}-${partner.sale_company_id}-${partnerIdx}`}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {partnerIdx === 0 && (
                          <TableCell
                            rowSpan={row.partners.length}
                            sx={{
                              width: 220,
                              minWidth: 220,
                              zIndex: 1,
                              verticalAlign: "middle",
                              backgroundColor: productGroupBg,
                              ...bodySx,
                            }}
                          >
                            {row.high_rank_company_name} / {row.product_name}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            width: 200,
                            minWidth: 200,
                            zIndex: 1,
                            backgroundColor: partnerRowBg,
                            ...bodySx,
                          }}
                        >
                          {partner.sale_company_name}
                        </TableCell>
                        {displayMode === "月別" && (
                          <TableCell
                            sx={{
                              width: 80,
                              minWidth: 80,
                              zIndex: 1,
                              backgroundColor: partnerRowBg,
                              ...bodySx,
                            }}
                          >
                            {partner.period_total ?? 0}
                          </TableCell>
                        )}
                        {displayMode === "日別" && (
                          <>
                            <TableCell
                              sx={{
                                width: 80,
                                minWidth: 80,
                                zIndex: 1,
                                backgroundColor: partnerRowBg,
                                ...bodySx,
                              }}
                            >
                              {(partner.month_total_shot ?? 0) + (partner.month_total_stock ?? 0)}
                            </TableCell>
                            <TableCell
                              sx={{
                                width: 80,
                                minWidth: 80,
                                zIndex: 1,
                                backgroundColor: partnerRowBg,
                                ...bodySx,
                              }}
                            >
                              {(partner.month_forecast_shot ?? 0) + (partner.month_forecast_stock ?? 0)}
                            </TableCell>
                          </>
                        )}
                        {(partner.counts ?? []).map((count, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              width: 80,
                              minWidth: 80,
                              backgroundColor: partnerRowBg,
                              ...bodySx,
                            }}
                          >
                            {count}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  }),
                );
              })()}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        data-testid="data-table-footer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
          py: 0.75,
          borderTop: "1px solid var(--color-border, #e5e5e0)",
          bgcolor: "background.paper",
          flexShrink: 0,
          fontSize: "11px",
          color: "var(--color-sumi-muted)",
        }}
      >
        <Box component="span">
          {totalProductCount !== undefined ? `${totalProductCount}件中` : ""}
          {pagedRows && pagedRows.length > 0 ? `${start}〜${end}件を表示` : ""}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {pages.map((p, i) =>
            p === "..." ? (
              <Box component="span" key={`ellipsis-${i}`} sx={{ fontSize: "12px", px: 0.5 }}>
                ...
              </Box>
            ) : (
              <Box
                key={p}
                onClick={() => { setPageNumber(p as number); fetchResult(undefined, p as number); }}
                sx={{
                  cursor: "pointer",
                  minWidth: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: p === pageNumber ? 600 : 400,
                  bgcolor: p === pageNumber ? "var(--color-ai)" : "transparent",
                  color: p === pageNumber ? "#fff" : "var(--color-sumi-light)",
                  "&:hover": {
                    bgcolor: p === pageNumber ? "var(--color-ai)" : "action.hover",
                  },
                }}
              >
                {p}
              </Box>
            ),
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span">
            表示数:
          </Box>
          {displayNumbers.map((n) => (
            <Box
              key={n}
              onClick={() => { setDisplayNumber(n); setPageNumber(1); fetchResult(n, 1); }}
              sx={{
                cursor: "pointer",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: n === displayNumber ? 600 : 400,
                color: "var(--color-sumi-light)",
                bgcolor: n === displayNumber ? "action.selected" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {n}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default TableProductPartnerResult;
